import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  Target,
  RefreshCw,
  Building2,
  Heart,
  ShoppingCart,
  TruckIcon,
  GraduationCap,
  Code,
  Database,
  Cloud,
  Shield,
  ArrowRight,
  CheckCircle,
} from 'lucide-react';

const Services = () => {
  const sectionsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fadeIn');
          }
        });
      },
      { threshold: 0.1 }
    );

    sectionsRef.current.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const mainServices = [
    {
      icon: Users,
      title: 'IT Staff Augmentation',
      description: 'Scale your team quickly and efficiently with our experienced IT professionals on a contract or project basis.',
      features: [
        'Rapid deployment of skilled resources',
        'Flexible contract terms (short-term to long-term)',
        'Expertise in modern technologies and frameworks',
        'Reduced hiring costs and overhead',
        'Seamless integration with existing teams',
      ],
      gradient: 'from-blue-500 to-cyan-500',
    },
    {
      icon: Target,
      title: 'Permanent IT Recruitment',
      description: 'Find full-time, committed technology professionals who align with your company culture and technical requirements.',
      features: [
        'Comprehensive candidate screening process',
        'Cultural fit assessment',
        'Technical skills evaluation',
        'Background verification and reference checks',
        'Post-placement support and follow-up',
      ],
      gradient: 'from-purple-500 to-pink-500',
    },
    {
      icon: RefreshCw,
      title: 'Contract-to-Hire Staffing',
      description: 'Test-fit employees before making a permanent commitment—ideal for dynamic or rapidly growing teams.',
      features: [
        'Risk-free trial period',
        'Evaluate performance before commitment',
        'Flexible conversion terms',
        'Reduced hiring risks',
        'Perfect for growing organizations',
      ],
      gradient: 'from-orange-500 to-yellow-500',
    },
  ];

  const industries = [
    { icon: Building2, title: 'Finance', description: 'Banking & fintech' },
    { icon: Heart, title: 'Healthcare', description: 'Health tech solutions' },
    { icon: ShoppingCart, title: 'E-Commerce', description: 'Digital commerce' },
    { icon: TruckIcon, title: 'Logistics', description: 'Supply chain tech' },
    { icon: GraduationCap, title: 'Education', description: 'EdTech platforms' },
  ];

  const techExpertise = [
    {
      icon: Code,
      title: 'Software Development',
      skills: ['Full-stack developers', 'Frontend specialists', 'Backend engineers', 'Mobile developers'],
    },
    {
      icon: Database,
      title: 'Data & Analytics',
      skills: ['Data scientists', 'Data engineers', 'Business analysts', 'BI specialists'],
    },
    {
      icon: Cloud,
      title: 'Cloud & DevOps',
      skills: ['Cloud architects', 'DevOps engineers', 'Site reliability engineers', 'Infrastructure specialists'],
    },
    {
      icon: Shield,
      title: 'Security & QA',
      skills: ['Security engineers', 'QA engineers', 'Test automation specialists', 'Cybersecurity experts'],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-purple-50 to-orange-50 opacity-60"></div>
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-purple-400/20 via-pink-400/20 to-orange-400/20 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              Comprehensive IT staffing{' '}
              <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-orange-500 bg-clip-text text-transparent">
                solutions
              </span>
            </h1>
            <p className="text-xl text-slate-600 mb-8">
              Tailored recruitment services designed to help you build high-performing technology teams.
            </p>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section
        ref={(el) => (sectionsRef.current[0] = el)}
        className="py-24 px-6 bg-white opacity-0 transition-opacity duration-1000"
      >
        <div className="max-w-7xl mx-auto space-y-24">
          {mainServices.map((service, index) => {
            const Icon = service.icon;
            return (
              <div
                key={index}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-6`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                    {service.title}
                  </h2>
                  <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-[#f9a825] mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact-us"
                    className="inline-flex items-center space-x-2 px-6 py-3 bg-[#f9a825] text-white rounded-full hover:bg-[#f57c00] transition-all duration-200 font-semibold group"
                  >
                    <span>Get Started</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                  </Link>
                </div>
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className={`bg-gradient-to-br ${service.gradient} p-12 rounded-3xl shadow-2xl`}>
                    <Icon className="w-32 h-32 text-white mx-auto opacity-30" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Industries We Serve */}
      <section
        ref={(el) => (sectionsRef.current[1] = el)}
        className="py-24 px-6 bg-slate-50 opacity-0 transition-opacity duration-1000"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Industries we serve
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Deep industry expertise enables us to match candidates with the right domain knowledge
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {industries.map((industry, index) => {
              const Icon = industry.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-6 hover:shadow-lg transition-all duration-300 text-center group border border-slate-200"
                >
                  <Icon className="w-10 h-10 text-slate-400 group-hover:text-[#f9a825] mx-auto mb-4 transition-colors duration-300" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">{industry.title}</h3>
                  <p className="text-sm text-slate-500">{industry.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Technical Expertise */}
      <section
        ref={(el) => (sectionsRef.current[2] = el)}
        className="py-24 px-6 bg-white opacity-0 transition-opacity duration-1000"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Technical expertise
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Access to a diverse pool of IT professionals with expertise across multiple domains
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {techExpertise.map((tech, index) => {
              const Icon = tech.icon;
              return (
                <div
                  key={index}
                  className="bg-slate-50 rounded-2xl p-8 hover:bg-slate-100 transition-all duration-300"
                >
                  <div className="flex items-center mb-6">
                    <Icon className="w-8 h-8 text-[#f9a825] mr-3" />
                    <h3 className="text-2xl font-bold text-slate-900">{tech.title}</h3>
                  </div>
                  <ul className="space-y-3">
                    {tech.skills.map((skill, sIndex) => (
                      <li key={sIndex} className="flex items-center">
                        <div className="w-1.5 h-1.5 bg-[#f9a825] rounded-full mr-3"></div>
                        <span className="text-slate-700">{skill}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        ref={(el) => (sectionsRef.current[3] = el)}
        className="py-24 px-6 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 opacity-0 transition-opacity duration-1000 relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-orange-500/10"></div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to find your perfect IT talent?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Contact us today to discuss your staffing needs
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact-us"
              className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-[#f9a825] text-white rounded-full hover:bg-[#f57c00] transition-all duration-200 font-semibold text-lg group"
            >
              <span>Get Started</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
            </Link>
            <a
              href="tel:+91-9121711433"
              className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-full border-2 border-white/20 hover:bg-white/20 transition-all duration-200 font-semibold text-lg"
            >
              <span>Call: +91-9121711433</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
